// src/engine/Scheduler.js
//
// Plays the tracks' MIDI clips against the transport. Message thread only.
//
// Every note is sent as an event at an absolute stream position, a little
// ahead of time (contract section 6.2, messaging.md 1.4), and the processor
// fires it in the block that contains it. Nothing here knows about blocks.
// A tick looks a fixed distance ahead of the clock, sends every note that
// starts inside that window, and remembers what it started so it can end it.
//
// Note offs come from that memory, not from the clips. A note deleted, moved
// or cut short while it sounds still has its off sent at the time it was
// given when it started, so no edit mid-play can leave a note held. Stopping
// ends everything still sounding, at the stop, or at the note's own start if
// that is still in the future.
//
// Looping unrolls the transport's loop into the timeline the clock runs on:
// elapsed time before the loop end plays as written, and each pass after it
// plays the loop again. A note that runs past the loop end is cut there.

import { automationBetween } from './Automation.js'
import { playedContent } from '../model/ClipLoop.js'
import { sounds } from './Chance.js'

const NOTE_ON = 0x90
const NOTE_OFF = 0x80
const CONTROL_CHANGE = 0xb0
const PITCH_BEND = 0xe0
/** The wheel at rest, as the two data bytes of a pitch bend message (14 bits, 8192 in the middle). */
const BEND_CENTRE = Uint8Array.from([PITCH_BEND, 0x00, 0x40])

/**
 * Every note the project would play, per node it plays into: each track's
 * MIDI clips, into that track's jig:midiInput. A track with none plays its
 * clips into nothing (project-format.md "Tracks"). Beats are absolute: a
 * note's start is its clip's start plus its own, and a note running past its
 * clip's end is cut there.
 *
 * Returns Map nodeId -> [{ on, off, pitch, velocity }], in beats.
 */
export function clipNotes (project) {
  const byNode = new Map()
  for (const track of project.tracks) {
    if (!track.midiInput) continue
    for (const clip of project.arrangementClips) {
      if (clip.track !== track.id || clip.kind !== 'midi' || clip.muted) continue
      const end = clip.startBeat + clip.lengthBeats
      for (const note of playedContent(clip).notes) {
        const on = clip.startBeat + note.startBeat
        if (on >= end) continue
        const off = Math.min(on + note.lengthBeats, end)
        if (!byNode.has(track.midiInput)) byNode.set(track.midiInput, [])
        byNode.get(track.midiInput).push({ on, off, pitch: note.pitch, velocity: note.velocity, ...(note.chance === undefined ? {} : { chance: note.chance }) })
      }
    }
  }
  return byNode
}

/**
 * Every controller and pitch bend event the project would play, per node, like clipNotes: each track's unmuted MIDI clips
 * into that track's jig:midiInput, at the clip's start plus the event's own beat, and none from at or after the clip's end.
 * Each carries the MIDI message to send; `off` equals `on` so the same windowing as a note's applies.
 *
 * Returns Map nodeId -> [{ on, off, bytes, bend, key }], in beats. `key` names what an event sets (the controller, or 'bend'),
 * so a player can find what each held at a moment.
 */
export function clipControllers (project) {
  const byNode = new Map()
  for (const track of project.tracks) {
    if (!track.midiInput) continue
    for (const clip of project.arrangementClips) {
      if (clip.track !== track.id || clip.kind !== 'midi' || clip.muted) continue
      const end = clip.startBeat + clip.lengthBeats
      for (const event of playedContent(clip).controllers) {
        const on = clip.startBeat + event.atBeat
        if (on >= end) continue
        const bend = event.controller === 'pitchBend'
        // A 14 bit value is sent least significant seven bits first, after being moved up so the middle is 8192.
        const bytes = bend
          ? Uint8Array.from([PITCH_BEND, (event.value + 8192) & 0x7f, (event.value + 8192) >> 7])
          : Uint8Array.from([CONTROL_CHANGE, event.controller, event.value])
        if (!byNode.has(track.midiInput)) byNode.set(track.midiInput, [])
        byNode.get(track.midiInput).push({ on, off: on, bytes, bend, key: bend ? 'bend' : event.controller })
      }
    }
  }
  return byNode
}

/**
 * Every audio clip the project would play, with where it starts and ends in
 * beats: [{ id, track, source, offsetSeconds, on, off }]. Where it plays into
 * is the page's to find, from the track.
 */
export function clipAudio (project) {
  return project.arrangementClips
    .filter(c => c.kind === 'audio' && !c.muted)
    .map(c => ({ id: c.id, track: c.track, source: c.source, offsetSeconds: c.offsetSeconds, fadeInBeats: c.fadeInBeats, fadeOutBeats: c.fadeOutBeats, on: c.startBeat, off: c.startBeat + c.lengthBeats }))
}

/**
 * The stretches of elapsed time, and what part of the song each plays.
 * Yields { from, to, lo, hi, shift }: elapsed [from, to) plays song seconds
 * [lo, hi), and a song time t is heard at elapsed t + shift. Only the
 * stretches that overlap elapsed [start, end).
 */
export function * segments (transport, start, end) {
  const loop = transport.loop
  if (!loop.enabled) {
    yield { from: 0, to: Infinity, lo: 0, hi: Infinity, shift: 0 }
    return
  }
  const loStart = transport.secondsAtBeat(loop.start)
  const loEnd = transport.secondsAtBeat(loop.end)
  const length = loEnd - loStart
  if (start < loEnd) yield { from: 0, to: loEnd, lo: 0, hi: loEnd, shift: 0 }
  // Straight to the first pass that matters, so an hour of looping costs the
  // same as the first pass.
  let k = start < loEnd ? 1 : 1 + Math.floor((start - loEnd) / length)
  for (;; k++) {
    const from = loEnd + (k - 1) * length
    if (from >= end) return
    yield { from, to: from + length, lo: loStart, hi: loEnd, shift: from - loStart }
  }
}

/**
 * The notes that start in elapsed [start, end), with when they start and end
 * in elapsed seconds. `notes` is one node's list from clipNotes, or the list
 * from clipAudio: anything with `on` and `off` in beats, whose other fields
 * are carried through.
 */
export function notesBetween (transport, notes, start, end) {
  const found = []
  const timed = notes.map(n => ({ ...n, onS: transport.secondsAtBeat(n.on), offS: transport.secondsAtBeat(n.off) }))
  for (const seg of segments(transport, start, end)) {
    for (const note of timed) {
      if (note.onS < seg.lo || note.onS >= seg.hi) continue
      const on = note.onS + seg.shift
      if (on < start || on >= end) continue
      const { onS, offS, ...rest } = note
      found.push({ ...rest, on, off: Math.min(offS, seg.hi) + seg.shift })
    }
  }
  return found
}

/**
 * The notes and controller events of the launcher's plays that fall in elapsed [start, end), as clipNotes and clipControllers give
 * them for the arrangement: `{ on, off }` in elapsed seconds. A play repeats its pattern every `length` beats from `from` until `to`;
 * a note is cut at the end of its pass and at `to`. The transport's loop is not applied: a launched pattern runs on the clock.
 */
export function launcherBetween (transport, plays, start, end) {
  const notes = new Map()
  const controllers = new Map()
  if (plays.length === 0) return { notes, controllers }
  const from = transport.beatAtSeconds(start)
  const until = transport.beatAtSeconds(end)
  const push = (map, nodeId, item) => { if (!map.has(nodeId)) map.set(nodeId, []); map.get(nodeId).push(item) }
  for (const play of plays) {
    const stop = play.to ?? Infinity
    const first = Math.max(0, Math.floor((from - play.from) / play.length))
    const last = Math.floor((Math.min(until, stop) - play.from) / play.length)
    for (let pass = first; pass <= last; pass++) {
      const origin = play.from + pass * play.length
      const passEnd = Math.min(origin + play.length, stop)
      for (const note of play.notes) {
        const on = origin + note.startBeat
        if (on < from || on >= until || on >= passEnd) continue
        const off = Math.min(on + note.lengthBeats, passEnd)
        push(notes, play.nodeId, { on: transport.secondsAtBeat(on), off: transport.secondsAtBeat(off), pitch: note.pitch, velocity: note.velocity, ...(note.chance === undefined ? {} : { chance: note.chance }) })
      }
      for (const event of play.controllers) {
        const on = origin + event.atBeat
        if (on < from || on >= until || on >= passEnd) continue
        const bend = event.controller === 'pitchBend'
        const bytes = bend
          ? Uint8Array.from([PITCH_BEND, (event.value + 8192) & 0x7f, (event.value + 8192) >> 7])
          : Uint8Array.from([CONTROL_CHANGE, event.controller, event.value])
        const at = transport.secondsAtBeat(on)
        push(controllers, play.nodeId, { on: at, off: at, bytes, bend })
      }
    }
  }
  return { notes, controllers }
}

/** An instruction from automationBetween with its times moved from elapsed seconds to the audio clock's. */
const onClock = (instruction, origin) => ({
  ...instruction,
  at: origin + instruction.at,
  ...(instruction.end !== undefined ? { end: origin + instruction.end } : {})
})

export class Scheduler {
  #now
  #sampleRate
  #lookahead
  #notes
  #controllers
  // The nodes that were sent a pitch bend, so stopping can bring the wheel back to rest.
  #bent = new Set()
  #transport
  #send
  #audio
  #playAudio
  #stopAudio
  #automation
  #launcher
  #origin = null
  #until = 0
  // Started and not yet ended: { nodeId, pitch, off (elapsed), onFrame }.
  #sounding = []
  // Where the song was entered, until the first tick has seeded the envelopes there; null otherwise.
  #seed = null

  /**
   * - `now()`: the audio clock in seconds, AudioContext.currentTime.
   * - `lookahead`: seconds ahead of the clock each tick schedules to. From
   *   configuration, never defaulted here.
   * - `notes()`: clipNotes of the project as it is now, read every tick so an
   *   edit is heard at the next one.
   * - `transport()`: the Transport as it is now.
   * - `send(nodeId, events)`: deliver events, `{ frame, bytes }`.
   * - `audio()`, `playAudio(clip, { when, offset, duration })` and
   *   `stopAudio()`: the audio clips, from clipAudio, and how to start and
   *   stop them. All three or none; a host that plays no audio clips gives none.
   * - `automation`: `{ envelopes(), apply(envelope, instruction), stop() }`, or nothing for a host that
   *   moves no parameters. `envelopes()` is `[{ id, target, points }]` as the project holds them; `apply`
   *   gets each instruction of Automation.js with its times on the audio clock.
   * - `launcher()`: the plays of the clip launcher as they are now (LauncherPlays.js), or nothing for a host without one. While a
   *   play covers a node, that node's arrangement notes and events inside the same beats are not played.
   */
  constructor ({ now, sampleRate, lookahead, notes, controllers, transport, send, audio, playAudio, stopAudio, automation, launcher }) {
    if (typeof now !== 'function' || typeof notes !== 'function' || typeof transport !== 'function' || typeof send !== 'function') {
      throw new Error('Scheduler needs now, notes, transport and send')
    }
    if (!(sampleRate > 0)) throw new Error('Scheduler needs a sample rate')
    if (!(lookahead > 0)) throw new Error('Scheduler needs a lookahead in seconds, from configuration')
    this.#now = now
    this.#sampleRate = sampleRate
    this.#lookahead = lookahead
    this.#notes = notes
    if (controllers !== undefined && typeof controllers !== 'function') throw new Error('Scheduler needs controllers as a function, or none')
    this.#controllers = controllers ?? null
    this.#transport = transport
    this.#send = send
    const given = [audio, playAudio, stopAudio].filter(f => f !== undefined)
    if (given.length !== 0 && (given.length !== 3 || given.some(f => typeof f !== 'function'))) {
      throw new Error('Scheduler needs all of audio, playAudio and stopAudio, or none of them')
    }
    this.#audio = audio ?? null
    this.#playAudio = playAudio ?? null
    this.#stopAudio = stopAudio ?? null
    if (automation !== undefined && ['envelopes', 'apply', 'stop'].some(k => typeof automation[k] !== 'function')) {
      throw new Error('Scheduler needs automation as { envelopes, apply, stop }, or none')
    }
    this.#automation = automation ?? null
    if (launcher !== undefined && typeof launcher !== 'function') throw new Error('Scheduler needs launcher as a function, or none')
    this.#launcher = launcher ?? null
  }

  get running () { return this.#origin !== null }

  /** An absolute stream position, from elapsed seconds since the start. */
  #frame (elapsed) {
    return Math.round((this.#origin + elapsed) * this.#sampleRate)
  }

  /**
   * Start at the clock time the transport's beat zero is heard.
   *
   * `from` is where in the song, in elapsed seconds, playing begins. Beat zero is then heard in the past, by that much, and
   * nothing before `from` is scheduled: a note that began earlier is not struck late. The first tick sets every envelope to
   * the value it has at `from`, and carries on from the middle of the segment it is in, as a loop's pass start does.
   */
  start (atTime, { from = 0 } = {}) {
    if (!(from >= 0)) throw new Error(`a start position is zero or more seconds: ${from}`)
    this.#origin = atTime
    this.#until = from
    this.#seed = from > 0 ? from : null
    this.#sounding = []
  }

  /** Schedule everything up to the lookahead. Call often; a tick with nothing new sends nothing. */
  tick () {
    if (!this.running) return
    const end = this.#now() - this.#origin + this.#lookahead
    const start = this.#until
    if (end <= start) return
    const transport = this.#transport()
    const outgoing = new Map()
    const add = (nodeId, frame, bytes, order) => {
      if (!outgoing.has(nodeId)) outgoing.set(nodeId, [])
      outgoing.get(nodeId).push({ frame, bytes, order })
    }

    // The stretches of song heard in this window. Where playing began part way in, the first is a pass that reads the song from
    // there, in place of the stretch of the first pass that has already gone by.
    const normal = [...segments(transport, start, end)]
    const passes = this.#seed === null
      ? normal
      : [{ from: this.#seed, to: normal[0]?.to ?? Infinity, lo: this.#seed, hi: normal[0]?.hi ?? Infinity, shift: 0 }, ...normal.filter(p => p.from > 0)]

    const plays = this.#launcher ? this.#launcher() : []
    const launched = launcherBetween(transport, plays, start, end)
    // The beats a launched slot covers on a node: the arrangement is silent there, so a loop can be put over a song.
    const covered = (nodeId, beat) => plays.some(p => p.nodeId === nodeId && beat >= p.from && beat < (p.to ?? Infinity))
    const playing = (nodeId, notes) => (plays.some(p => p.nodeId === nodeId) ? notes.filter(n => !covered(nodeId, n.on)) : notes)
    for (const [nodeId, listed] of this.#notes()) {
      const notes = playing(nodeId, listed)
      for (const note of notesBetween(transport, notes, start, end)) {
        // A note with a chance sounds or not by a draw from where and when it falls (Chance.js).
        if (!sounds(note.chance, `${nodeId}|${note.pitch}|${note.on.toFixed(6)}`)) continue
        const onFrame = this.#frame(note.on)
        add(nodeId, onFrame, Uint8Array.from([NOTE_ON, note.pitch, note.velocity]), 1)
        this.#sounding.push({ nodeId, pitch: note.pitch, off: note.off, onFrame })
      }
    }
    for (const [nodeId, list] of launched.notes) {
      for (const note of list) {
        if (!sounds(note.chance, `${nodeId}|${note.pitch}|${note.on.toFixed(6)}`)) continue
        const onFrame = this.#frame(note.on)
        add(nodeId, onFrame, Uint8Array.from([NOTE_ON, note.pitch, note.velocity]), 1)
        this.#sounding.push({ nodeId, pitch: note.pitch, off: note.off, onFrame })
      }
    }
    for (const [nodeId, list] of launched.controllers) {
      for (const event of list) {
        add(nodeId, this.#frame(event.on), event.bytes, 0.5)
        if (event.bend) this.#bent.add(nodeId)
      }
    }
    // Controllers and the pitch bend wheel, in the same windows. At one frame they come after the offs and before the ons,
    // so a note struck on the beat is struck with the new value.
    if (this.#controllers) {
      for (const [nodeId, listed] of this.#controllers()) {
        const events = playing(nodeId, listed)
        for (const event of notesBetween(transport, events, start, end)) {
          add(nodeId, this.#frame(event.on), event.bytes, 0.5)
          if (event.bend) this.#bent.add(nodeId)
        }
        // A controller holds its value, so where a pass begins part way through the song (a loop coming round, or playing started
        // there) each is set to what it held at that moment, before the first note of the pass. A wheel with nothing earlier to
        // say goes back to rest, since the end of the last pass may have left it up.
        for (const pass of passes) {
          if (!(pass.from >= start && pass.from < end) || !(pass.lo > 0)) continue
          const held = new Map()
          for (const e of [...events].sort((a, b) => a.on - b.on)) if (transport.secondsAtBeat(e.on) < pass.lo) held.set(e.key, e)
          for (const e of events) if (e.bend && !held.has('bend')) held.set('bend', { ...e, bytes: BEND_CENTRE })
          for (const e of held.values()) {
            add(nodeId, this.#frame(pass.from), e.bytes, 0.25)
            if (e.bend) this.#bent.add(nodeId)
          }
        }
      }
    }
    // Audio clips start at the same stream positions, through the platform's
    // own scheduling: a source started at a context time, not an event.
    if (this.#audio) {
      for (const clip of notesBetween(transport, this.#audio(), start, end)) {
        this.#playAudio(clip, { when: this.#origin + clip.on, offset: clip.offsetSeconds, duration: clip.off - clip.on })
      }
    }
    // Envelopes move a parameter on the audio clock: the same windows, looped the same way as the notes.
    if (this.#automation) {
      for (const envelope of this.#automation.envelopes()) {
        for (const instruction of automationBetween(transport, envelope.points, passes, start, end)) {
          this.#automation.apply(envelope, onClock(instruction, this.#origin))
        }
      }
    }
    this.#seed = null
    this.#sounding = this.#sounding.filter(s => {
      if (s.off >= end) return true
      add(s.nodeId, Math.max(this.#frame(s.off), s.onFrame), Uint8Array.from([NOTE_OFF, s.pitch, 0]), 0)
      return false
    })
    this.#until = end
    this.#deliver(outgoing)
  }

  /** End everything still sounding, and stop. */
  stop () {
    if (!this.running) return
    const nowFrame = this.#frame(this.#now() - this.#origin)
    const outgoing = new Map()
    for (const s of this.#sounding) {
      if (!outgoing.has(s.nodeId)) outgoing.set(s.nodeId, [])
      outgoing.get(s.nodeId).push({ frame: Math.max(nowFrame, s.onFrame), bytes: Uint8Array.from([NOTE_OFF, s.pitch, 0]), order: 0 })
    }
    for (const nodeId of this.#bent) {
      if (!outgoing.has(nodeId)) outgoing.set(nodeId, [])
      outgoing.get(nodeId).push({ frame: nowFrame, bytes: BEND_CENTRE, order: 2 })
    }
    this.#bent.clear()
    this.#sounding = []
    this.#origin = null
    this.#stopAudio?.()
    this.#automation?.stop()
    this.#deliver(outgoing)
  }

  /** In time order, and at one frame an off before an on, so a repeated note is retriggered rather than cut. */
  #deliver (outgoing) {
    for (const [nodeId, events] of outgoing) {
      events.sort((a, b) => a.frame - b.frame || a.order - b.order)
      this.#send(nodeId, events.map(({ frame, bytes }) => ({ frame, bytes })))
    }
  }
}
